import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "api/baseurl";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import {Typography,} from "@mui/material";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import TextField from "@mui/material/TextField";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { decryptBody } from "encryptionAndDecryption/bodyDecryption";
import { fetchSecretKey } from "encryptionAndDecryption/secretKeyDecryption";
import { encryptBody } from "encryptionAndDecryption/bodyEncryption";
import MedicalAbstractBackground from "./MedicalAbstractBackground ";

const CustomStepper = ({ title }) => {
  return (
    <Box mb={3} display="flex" justifyContent="center">
      <Typography
        variant="h6"
        sx={{ fontWeight: "bold", color: "#2e7fae", textAlign: "center" }}
      >
        {title}
      </Typography>
    </Box>
  );
};

function UpdateThreshold() {
  const navigate = useNavigate();
  const { id } = useParams(); // ✅ Get ID from URL

  const [listDetails, setListDetails] = useState({
    
    patientid: "",
    systolicLow: "",
    systolicHigh: "",
    diastolicLow: "",
    diastolicHigh: "",
    glucoseHigh: "",
    glucoseLow: "",
  });

  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setListDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchThreshold = async () => {
    try {
      setLoading(true);
      const secretKey = await fetchSecretKey();
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;

      const response = await axios.get(
        `/api/patient-threshold/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200) {
        const decryptedData = decryptBody(response.data, secretKey);

        setListDetails({
          id: decryptedData.id,
          patientid: decryptedData.patientid,
          systolicHigh: decryptedData.systolicHigh || "",
          systolicLow: decryptedData.systolicLow || "",
          diastolicHigh: decryptedData.diastolicHigh || "",
          diastolicLow: decryptedData.diastolicLow || "",
          glucoseHigh: decryptedData.glucoseHigh || "",
          glucoseLow: decryptedData.glucoseLow || "",
        });
      }
    } catch (error) {
      console.error("Error fetching threshold details:", error);
      toast.error("Failed to fetch threshold details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchThreshold();
    }
  }, [id]);

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const secretKey = await fetchSecretKey();
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;

      const dataToSend = {
       
        patientid: listDetails.patientid,
        systolicHigh: Number(listDetails.systolicHigh),
        systolicLow: Number(listDetails.systolicLow),
        diastolicHigh: Number(listDetails.diastolicHigh),
        diastolicLow: Number(listDetails.diastolicLow),
        glucoseHigh: Number(listDetails.glucoseHigh),
        glucoseLow: Number(listDetails.glucoseLow),
      };

      const encryptedData = encryptBody(dataToSend, secretKey);

      const response = await axios.post(
        "/api/patient-threshold/update",
        encryptedData,
        {
          headers: {
            "Content-Type": "text/plain",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success("Threshold Details updated successfully!");

        // ✅ Redirect after update
        navigate(-1); // goes back to previous page
        // OR navigate("/patient-list");
      }
    } catch (error) {
      toast.error("Failed to update Threshold Details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MedicalAbstractBackground />

      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
        p={2}
      >
        <Card
          sx={{
            width: { xs: "95%", sm: "90%", md: "800px" },
            padding: 4,
            borderRadius: 3,
          }}
        >
                      <CustomStepper title="Update Threshold Details
" />


          <form onSubmit={handleSubmitEdit}>
            <Grid container spacing={3}>
              {[
                "systolicLow",
                "systolicHigh",
                "diastolicLow",
                "diastolicHigh",
                "glucoseLow",
                "glucoseHigh",
              ].map((field) => (
                <Grid item xs={12} sm={6} key={field}>
                  <TextField
                    type="number"
                    label={field}
                    name={field}
                    fullWidth
                    value={listDetails[field] || ""}
                    onChange={handleInputChange}
                  />
                </Grid>
              ))}
            </Grid>

            <MDBox mt={4} textAlign="center">
              <MDButton
                variant="gradient"
                sx={{
                  background: "#1A237E",
                  color: "#fff",
                  "&:hover": { background: "#0D1B60" },
                }}
                type="submit"
              >
                Update Threshold
              </MDButton>
              &emsp;
              <MDButton
                variant="outlined"
                color="secondary"
                onClick={() => navigate(-1)}
              >
                Cancel
              </MDButton>
            </MDBox>
          </form>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default UpdateThreshold;